import { LayoutDashboard, Files, Heart, Trash2, PlayCircle, Sparkles, LogOut, Settings } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useState } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { useTheme } from 'next-themes';
import { useSubscription } from '../hooks/useSubscription';
import { PricingDialog } from './pricing-dialog';

interface SidebarProps {
  activeNav?: string;
  onNavigate?: (nav: string) => void;
}

export function Sidebar({ activeNav = 'properties', onNavigate }: SidebarProps) {
  const { user, signOut, profile: authProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  // Use subscription hook for real data
  const {
    creditsAvailable: subCreditsAvailable, // Alias to avoid conflict, we typically ignore this one now
    creditsLimit,
    planDisplayName,
    isActive,
    getLemonSqueezyCheckoutUrl
  } = useSubscription();

  // Use AuthContext for real-time credits, falling back to subscription hook if needed (but auth is better)
  const creditsAvailable = authProfile?.available_credits ?? subCreditsAvailable;

  // Transform DB profile to UI profile
  const profile = authProfile ? {
    firstName: authProfile.first_name,
    lastName: authProfile.last_name,
    avatarUrl: authProfile.avatar_url,
    subscriptionStatus: authProfile.subscription_status,
    availableCredits: authProfile.available_credits
  } : null;

  // Determine plan display name
  const subscriptionPlan = planDisplayName.toUpperCase();

  // Get user initials from email or name
  const userEmail = user?.email || 'user@example.com';

  const displayName = profile?.firstName && profile?.lastName
    ? `${profile.firstName} ${profile.lastName}`
    : profile?.firstName
      ? profile.firstName
      : userEmail;

  const userInitials = profile?.firstName
    ? profile.firstName[0].toUpperCase() + (profile.lastName?.[0]?.toUpperCase() || '')
    : userEmail.slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    setIsProfileOpen(false);
    await signOut();
  };

  return (
    <aside className="hidden lg:flex fixed left-6 top-6 bottom-6 w-[300px] rounded-[24px] bg-sidebar border border-sidebar-border shadow-[0_8px_28px_rgba(0,0,0,0.06)] flex-col z-50 overflow-hidden">
      {/* Header */}
      <div className="px-8 py-8 mb-2">
        <h1 className="text-[28px] font-bold tracking-tight text-sidebar-foreground" style={{ fontFamily: 'Clash Grotesk, sans-serif' }}>WYSKO</h1>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-5 flex flex-col gap-2">
        {/* ... navigation buttons ... */}
        <div className="space-y-1">
          <button
            onClick={() => onNavigate?.('properties')}
            className={`w-full flex items-center gap-3.5 text-left px-5 py-3.5 rounded-xl text-[15px] font-medium transition-all duration-200 ${activeNav === 'properties'
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
              : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              }`}
          >
            <LayoutDashboard className="w-5 h-5" strokeWidth={activeNav === 'properties' ? 2 : 1.5} />
            <span>Moje nieruchomości</span>
          </button>

          <button
            onClick={() => onNavigate?.('allphotos')}
            className={`w-full flex items-center gap-3.5 text-left px-5 py-3.5 rounded-xl text-[15px] font-medium transition-all duration-200 ${activeNav === 'allphotos'
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
              : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              }`}
          >
            <Files className="w-5 h-5" strokeWidth={activeNav === 'allphotos' ? 2 : 1.5} />
            <span>Wszystkie pliki</span>
          </button>

          <button
            onClick={() => onNavigate?.('favorites')}
            className={`w-full flex items-center gap-3.5 text-left px-5 py-3.5 rounded-xl text-[15px] font-medium transition-all duration-200 ${activeNav === 'favorites'
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
              : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              }`}
          >
            <Heart className="w-5 h-5" strokeWidth={activeNav === 'favorites' ? 2 : 1.5} />
            <span>Ulubione</span>
          </button>

          <button
            onClick={() => onNavigate?.('trash')}
            className={`w-full flex items-center gap-3.5 text-left px-5 py-3.5 rounded-xl text-[15px] font-medium transition-all duration-200 ${activeNav === 'trash'
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
              : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              }`}
          >
            <Trash2 className="w-5 h-5" strokeWidth={activeNav === 'trash' ? 2 : 1.5} />
            <span>Kosz</span>
          </button>
        </div>

        {/* Bottom Navigation - Knowledge Base */}
        <div className="mt-auto pb-4">
          <div className="space-y-1">
            <div
              className="w-full flex items-center gap-3.5 text-left px-5 py-3.5 rounded-xl text-[15px] font-medium text-muted-foreground/50 cursor-not-allowed"
              title="Dostępne wkrótce"
            >
              <PlayCircle className="w-5 h-5" strokeWidth={1.5} />
              <span>Tutoriale</span>
              <span className="ml-auto text-[10px] text-muted-foreground/40 uppercase tracking-wider">Wkrótce</span>
            </div>

            <div
              className="w-full flex items-center gap-3.5 text-left px-5 py-3.5 rounded-xl text-[15px] font-medium text-muted-foreground/50 cursor-not-allowed"
              title="Dostępne wkrótce"
            >
              <Sparkles className="w-5 h-5" strokeWidth={1.5} />
              <span>Inspiracje</span>
              <span className="ml-auto text-[10px] text-muted-foreground/40 uppercase tracking-wider">Wkrótce</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Footer */}
      <div className="px-4 pb-6 space-y-3">
        {/* Credits Widget - Redesigned */}
        <div className={`mx-3 px-4 py-3.5 rounded-lg border transition-all duration-300 ${creditsAvailable === 0
          ? 'bg-red-500/5 border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.15)]'
          : 'bg-muted/30 border-border/50'
          }`}>
          <div className="text-[9px] text-muted-foreground uppercase tracking-[0.1em] mb-2">
            {subscriptionPlan}
          </div>
          <div className={`text-[16px] tracking-tight font-semibold mb-2.5 ${creditsAvailable === 0 ? 'text-red-500' : ''}`}>
            {creditsAvailable} / {creditsLimit === 0 ? '∞' : creditsLimit}
          </div>
          {/* Progress Bar */}
          <div className="w-full h-1 bg-muted rounded-full overflow-hidden mb-2.5">
            <div
              className={`h-full transition-all duration-300 ${creditsAvailable === 0 ? 'bg-red-500' :
                (creditsLimit > 0 && (creditsAvailable / creditsLimit) < 0.2) ? 'bg-red-500' :
                  (creditsLimit > 0 && (creditsAvailable / creditsLimit) < 0.4) ? 'bg-yellow-500' :
                    'bg-green-500'
                }`}
              style={{ width: creditsLimit > 0 ? `${Math.min((creditsAvailable / creditsLimit) * 100, 100)}%` : '100%' }}
            />
          </div>
          {/* CTA Link */}
          <button
            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
            onClick={() => setIsPricingOpen(true)}
          >
            Zwiększ limit
          </button>
        </div>

        {/* Pricing Dialog */}
        <PricingDialog open={isPricingOpen} onOpenChange={setIsPricingOpen} />

        {/* User Profile - With Popover */}
        <Popover open={isProfileOpen} onOpenChange={setIsProfileOpen}>
          <PopoverTrigger asChild>
            <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors">
              <Avatar className="w-9 h-9 border border-border">
                {profile?.avatarUrl && <AvatarImage src={profile.avatarUrl} />}
                <AvatarFallback className="bg-primary text-primary-foreground text-[12px] font-medium">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[13px] truncate font-medium">{displayName}</div>
                {(profile?.firstName || profile?.lastName) && (
                  <div className="text-[10px] text-muted-foreground truncate">{userEmail}</div>
                )}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[248px] p-0 mb-2"
            side="top"
            align="start"
            sideOffset={8}
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-border">
              <Avatar className="w-10 h-10 border border-border">
                {profile?.avatarUrl && <AvatarImage src={profile.avatarUrl} />}
                <AvatarFallback className="bg-primary text-primary-foreground text-[12px] font-medium">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">{displayName}</div>
                <div className="text-[11px] text-muted-foreground truncate">Użytkownik Wysko</div>
              </div>
            </div>

            {/* Body - Dark Mode Toggle */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <Label htmlFor="dark-mode" className="text-[13px] cursor-pointer">
                  Tryb Ciemny
                </Label>
                <Switch
                  id="dark-mode"
                  checked={isDarkMode}
                  onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                />
              </div>
            </div>

            {/* Settings Button */}
            <div className="p-2 border-b border-border">
              <Button
                variant="ghost"
                className="w-full justify-start text-[13px] text-muted-foreground hover:text-foreground"
                onClick={() => {
                  onNavigate?.('settings');
                  setIsProfileOpen(false);
                }}
              >
                <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Ustawienia
              </Button>
            </div>

            {/* Footer - Logout */}
            <div className="p-2">
              <Button
                variant="ghost"
                className="w-full justify-start text-[13px] text-muted-foreground hover:text-foreground"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Wyloguj
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </aside>
  );
}